import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Keeps heavy dialog bodies (big forms) from holding up the slide animation.
 * - Open: mount the children two frames after the sheet starts sliding. The slide is a transform
 *   transition, so it keeps running on the compositor while React builds the content — instead of
 *   the whole screen freezing until the content is built and only then starting the slide.
 * - Close: keep rendering the last open children. Same element objects → React skips that subtree,
 *   so the exit slide isn't held up re-rendering the content (and the content doesn't flicker while
 *   its owner resets state).
 */
export function useSheetContent(open: boolean, keepMounted: boolean | undefined, children: ReactNode) {
  const [ready, setReady] = useState(false);
  const lastOpenChildren = useRef(children);
  if (open) lastOpenChildren.current = children;

  useEffect(() => {
    if (!open) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setReady(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [open]);

  /** Call once the exit slide has finished. */
  const onExited = () => {
    if (!keepMounted) setReady(false);
  };

  return { content: ready ? lastOpenChildren.current : null, onExited };
}
